import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AgentEvents,
  ProjectAppConsideringEvent,
  ProjectAppLoadingEvent,
} from '@dreamai/types';
import { usePusher } from '../contexts/PusherContext';

type AppLoadingPhase = 'considering' | 'loading';

interface AppLoadingState {
  appId: string;
  phase: AppLoadingPhase;
}

interface ProjectAppLoadingContextType {
  /** The currently loading app, or null if nothing is loading */
  loadingApp: AppLoadingState | null;
  /** Check if a specific app is currently in any active phase */
  isLoading: (appId: string) => boolean;
  /** Check if a specific app is in the considering phase */
  isConsidering: (appId: string) => boolean;
  /** Counter that increments each time a PVA run finishes — consumers can re-fetch */
  finishedKey: number;
}

const ProjectAppLoadingContext =
  createContext<ProjectAppLoadingContextType | null>(null);

export const useProjectAppLoading = (): ProjectAppLoadingContextType => {
  const context = useContext(ProjectAppLoadingContext);
  if (!context) {
    throw new Error(
      'useProjectAppLoading must be used within a ProjectAppLoadingProvider',
    );
  }
  return context;
};

interface InitialAppStatus {
  appId: string;
  updateStatus?: string;
}

interface ProjectAppLoadingProviderProps {
  children: React.ReactNode;
  initialAppStatuses?: InitialAppStatus[];
}

export const ProjectAppLoadingProvider: React.FC<
  ProjectAppLoadingProviderProps
> = ({ children, initialAppStatuses }) => {
  const [searchParams] = useSearchParams();
  const threadId = searchParams.get('thread_id');
  const pusherContext = usePusher(true);
  const [loadingApp, setLoadingApp] = useState<AppLoadingState | null>(null);
  const [finishedKey, setFinishedKey] = useState(0);
  const [initializedFromApi, setInitializedFromApi] = useState(false);

  // Listen for PROJECT_APP_CONSIDERING on the company channel
  useEffect(() => {
    if (!threadId || !pusherContext?.pusherChannel) return;

    const handleConsidering = (data: ProjectAppConsideringEvent) => {
      if (data.threadId !== threadId) return;
      setLoadingApp({
        appId: data.appId,
        phase: 'considering',
      });
    };

    pusherContext.pusherChannel.bind(
      AgentEvents.PROJECT_APP_CONSIDERING,
      handleConsidering,
    );

    return () => {
      pusherContext.pusherChannel?.unbind(
        AgentEvents.PROJECT_APP_CONSIDERING,
        handleConsidering,
      );
    };
  }, [threadId, pusherContext?.pusherChannel]);

  // Listen for PROJECT_APP_LOADING on the company channel
  useEffect(() => {
    if (!threadId || !pusherContext?.pusherChannel) return;

    const handleLoading = (data: ProjectAppLoadingEvent) => {
      if (data.threadId !== threadId) return;
      setLoadingApp({
        appId: data.appId,
        phase: 'loading',
      });
    };

    pusherContext.pusherChannel.bind(
      AgentEvents.PROJECT_APP_LOADING,
      handleLoading,
    );

    return () => {
      pusherContext.pusherChannel?.unbind(
        AgentEvents.PROJECT_APP_LOADING,
        handleLoading,
      );
    };
  }, [threadId, pusherContext?.pusherChannel]);

  // Listen for PROJECT_APP_LOADING_FINISHED on the company channel
  useEffect(() => {
    if (!threadId || !pusherContext?.pusherChannel) return;

    const handleLoadingFinished = (data: { threadId: string }) => {
      if (data.threadId !== threadId) return;
      console.log(
        '[ProjectAppLoading] Received LOADING_FINISHED event for thread:',
        threadId,
      );
      setFinishedKey((k) => k + 1);
      setLoadingApp(null);
    };

    pusherContext.pusherChannel.bind(
      AgentEvents.PROJECT_APP_LOADING_FINISHED,
      handleLoadingFinished,
    );

    return () => {
      pusherContext.pusherChannel?.unbind(
        AgentEvents.PROJECT_APP_LOADING_FINISHED,
        handleLoadingFinished,
      );
    };
  }, [threadId, pusherContext?.pusherChannel]);

  // Seed loading state from API data on initial load (or when projectApps arrive)
  useEffect(() => {
    if (initializedFromApi || !initialAppStatuses?.length) return;
    const active = initialAppStatuses.find(
      (a) => a.updateStatus === 'considering' || a.updateStatus === 'loading',
    );
    if (active) {
      setLoadingApp({
        appId: active.appId,
        phase: active.updateStatus as AppLoadingPhase,
      });
    }
    setInitializedFromApi(true);
  }, [initialAppStatuses, initializedFromApi]);

  // Clear loading state when thread changes
  useEffect(() => {
    setLoadingApp(null);
    setInitializedFromApi(false);
  }, [threadId]);

  const isLoading = useCallback(
    (appId: string) => loadingApp?.appId === appId,
    [loadingApp],
  );

  const isConsidering = useCallback(
    (appId: string) =>
      loadingApp?.appId === appId && loadingApp?.phase === 'considering',
    [loadingApp],
  );

  return (
    <ProjectAppLoadingContext.Provider
      value={{
        loadingApp,
        isLoading,
        isConsidering,
        finishedKey,
      }}
    >
      {children}
    </ProjectAppLoadingContext.Provider>
  );
};
